import React, { lazy } from "react";

export type Tier = "starter" | "pro" | "enterprise" | "internal";

export const TIER_ORDER: Record<Tier, number> = {
  starter: 0,
  pro: 1,
  enterprise: 2,
  internal: 3,
};

export interface SettingsPage {
  id: string;
  label: string;
  component: React.LazyExoticComponent<React.ComponentType<unknown>>;
  legacyAliases?: string[];
}

export interface SettingsSection {
  id: string;
  label: string;
  description: string;
  tier: Tier;
  icon: React.ReactNode;
  pages: SettingsPage[];
}

const LEGACY_HIDDEN_SETTINGS_REDIRECTS: Record<string, string> = {
  "/admin/integrations": "/settings/connect/ai",
  "/admin/data-sources": "/settings/connect/accounts",
  "/admin/agent-email": "/settings/connect/accounts",
  "/admin/approvals": "/",
  "/admin/capabilities": "/settings/security/controls",
  "/admin/workspace-access": "/settings/security/controls",
};

const LazySettings = lazy(() => import("../pages/Settings"));
const LazySubscriptions = lazy(() => import("../pages/Subscriptions"));
const LazyConnectedAccounts = lazy(() => import("../pages/ConnectedAccounts"));
const LazyUsage = lazy(() => import("../pages/Usage"));

const icons = {
  connections: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <circle cx="4" cy="4" r="2" />
      <circle cx="12" cy="4" r="2" />
      <circle cx="8" cy="12" r="2" />
      <path d="M5.5 5.5L7 10.5M10.5 5.5L9 10.5" />
    </svg>
  ),
  usage: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path d="M2 14V6l3-2 3 4 3-6 3 4v8H2z" />
    </svg>
  ),
  security: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path d="M8 1L2 4v4c0 3.5 2.6 6.5 6 7.5 3.4-1 6-4 6-7.5V4L8 1z" />
    </svg>
  ),
};

export const SETTINGS_SECTIONS: SettingsSection[] = [
  {
    id: "connect",
    label: "Connect",
    description: "Choose the AI and accounts your agent should use",
    tier: "starter",
    icon: icons.connections,
    pages: [
      {
        id: "ai",
        label: "AI",
        component: LazySubscriptions,
      },
      {
        id: "accounts",
        label: "Email & calendar",
        component: LazyConnectedAccounts,
      },
    ],
  },
  {
    id: "usage",
    label: "Usage",
    description: "See what your agent is using and what it costs",
    tier: "starter",
    icon: icons.usage,
    pages: [{ id: "usage", label: "Usage", component: LazyUsage, legacyAliases: ["/admin/usage"] }],
  },
  {
    id: "security",
    label: "Security",
    description: "Pause your agent, set limits, and adjust the rules it follows",
    tier: "starter",
    icon: icons.security,
    pages: [
      {
        id: "controls",
        label: "Pause & limits",
        component: LazySettings,
        legacyAliases: ["/admin/settings"],
      },
    ],
  },
];

export function getDefaultSettingsTarget(): { sectionId: string; pageId: string } {
  const firstSection = SETTINGS_SECTIONS[0];
  return {
    sectionId: firstSection?.id ?? "connect",
    pageId: firstSection?.pages[0]?.id ?? "ai",
  };
}

export function getAccessibleSettingsSections(tier: Tier): SettingsSection[] {
  return SETTINGS_SECTIONS.filter((section) => TIER_ORDER[section.tier] <= TIER_ORDER[tier]);
}

export function findSection(sectionId: string | undefined): SettingsSection | undefined {
  if (!sectionId) return undefined;
  return SETTINGS_SECTIONS.find((section) => section.id === sectionId);
}

export function findPage(
  sectionId: string | undefined,
  pageId: string | undefined,
): SettingsPage | undefined {
  const section = findSection(sectionId);
  if (!section || !pageId) return undefined;
  return section.pages.find((page) => page.id === pageId);
}

export function buildSettingsPath(sectionId?: string, pageId?: string): string {
  if (!sectionId) return "/settings";
  if (!pageId) return `/settings/${sectionId}`;
  return `/settings/${sectionId}/${pageId}`;
}

export function resolveLegacyAdminPath(pathname: string): string | null {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  if (normalized === "/admin" || normalized === "/admin/setup") {
    return "/settings";
  }
  if (normalized === "/admin/chat") {
    return "/";
  }
  if (LEGACY_HIDDEN_SETTINGS_REDIRECTS[normalized]) {
    return LEGACY_HIDDEN_SETTINGS_REDIRECTS[normalized];
  }

  for (const section of SETTINGS_SECTIONS) {
    for (const page of section.pages) {
      if (page.legacyAliases?.includes(normalized)) {
        return buildSettingsPath(section.id, page.id);
      }
    }
  }

  return null;
}
